import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Divider
} from '@mui/material';
import {
  MoreVert,
  TrendingUp,
  BugReport,
  CheckCircle,
  Warning,
  Error,
  BarChart,
  Timeline,
  PieChart,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import Footer from '../components/Footer';
import logo from '../assets/logo0.png';
import { Link, useNavigate } from 'react-router-dom';

const Reports = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const navigate = useNavigate();

  // Analytics Data
  const analyticsData = {
    totalReports: 156,
    resolvedReports: 124,
    pendingReports: 22,
    criticalIssues: 10,
    resolutionRate: 79,
    averageResponseTime: '2.5 hours'
  };

  // Sample Reports Data
  const reports = [
    {
      id: 1,
      title: 'Server Performance Issue',
      status: 'Critical',
      date: '2024-02-20',
      category: 'Performance',
      assignee: 'John Doe',
      priority: 'High'
    },
    // Add more sample data...
  ];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      Critical: 'error',
      High: 'warning',
      Medium: 'info',
      Low: 'success'
    };
    return colors[status] || 'default';
  };

  // Add navigation links array
  const navLinks = [
    { name: 'AI DETECT', path: '/ai-detect' },
    { name: 'SHOP', path: '/marketplace' },
    { name: 'REPORT', path: '/report' },
    { name: 'SCANS HISTORY', path: '/scan-history' }
  ];

  const handleViewDetails = (reportId) => {
    navigate(`/report-view/${reportId}`);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#000000', p: 2 }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            {/* Logo with redirect */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                cursor: 'pointer' 
              }}
              onClick={() => navigate('/dashboard')}
            >
              <img 
                src={logo} 
                alt="Logo" 
                style={{ 
                  height: 30, // Reduced from 40 to 30
                  filter: 'brightness(0) invert(1)'
                }} 
              />
            </Box>

            {/* Navigation Links */}
            <Box sx={{ 
              display: 'flex', 
              gap: 3, 
              alignItems: 'center',
              flex: 1,
              ml: 4
            }}>
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  style={{ 
                    textDecoration: 'none',
                    color: 'white',
                    fontWeight: 500,
                    fontSize: '14px'
                  }}
                >
                  {link.name}
                </Link>
              ))}
            </Box>

            {/* Profile Section */}
            <Box>
              <Tooltip title="Account settings">
                <IconButton
                  onClick={handleProfileMenuOpen}
                  size="small"
                  sx={{ color: 'white' }}
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: '#007C3F' }}>
                    <PersonIcon />
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            '& .MuiMenuItem-root': {
              typography: 'body2',
              minHeight: 40,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => navigate('/profile')}>
          <PersonIcon sx={{ mr: 2 }} /> Profile
        </MenuItem>
        <MenuItem onClick={() => navigate('/settings')}>
          <SettingsIcon sx={{ mr: 2 }} /> Settings
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => {
            // Add your logout logic here
            navigate('/login');
          }} 
          sx={{ color: 'error.main' }}
        >
          <LogoutIcon sx={{ mr: 2 }} /> Logout
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flex: 1 }}>
        {/* Analytics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Reports
                </Typography>
                <Typography variant="h4">
                  {analyticsData.totalReports}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={70} 
                  sx={{ mt: 2, bgcolor: '#e0e0e0' }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Resolution Rate
                </Typography>
                <Typography variant="h4">
                  {analyticsData.resolutionRate}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={analyticsData.resolutionRate} 
                  color="success"
                  sx={{ mt: 2 }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pending Reports
                </Typography>
                <Typography variant="h4">
                  {analyticsData.pendingReports}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={30} 
                  color="warning"
                  sx={{ mt: 2 }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Critical Issues
                </Typography>
                <Typography variant="h4">
                  {analyticsData.criticalIssues}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={45} 
                  color="error"
                  sx={{ mt: 2 }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Reports Table */}
        <Paper sx={{ width: '100%', mb: 4 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>Report ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Assignee</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((report) => (
                    <TableRow 
                      key={report.id} 
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleViewDetails(report.id)}
                    >
                      <TableCell>#{report.id}</TableCell>
                      <TableCell>{report.title}</TableCell>
                      <TableCell>
                        <Chip 
                          label={report.status} 
                          color={getStatusColor(report.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{report.date}</TableCell>
                      <TableCell>{report.category}</TableCell>
                      <TableCell>{report.assignee}</TableCell>
                      <TableCell>
                        <Chip 
                          label={report.priority}
                          color={getStatusColor(report.priority)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={handleMenuClick}>
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={reports.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Container>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleViewDetails(reports[page * rowsPerPage]?.id);
          handleMenuClose();
        }}>View Details</MenuItem>
        <MenuItem onClick={handleMenuClose}>Edit Report</MenuItem>
        <MenuItem onClick={handleMenuClose}>Delete Report</MenuItem>
      </Menu>

      <Footer />
    </Box>
  );
};

export default Reports; 